import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';
import { Button } from '@shared/ui';
import type { ComponentProps, FC } from 'react';

const gradientBackground =
  'linear-gradient(159.48deg, rgba(255, 132, 98, 1) 0%, rgba(221, 162, 217, 1) 98.18%)';

type GradientActionButtonProps = Omit<ComponentProps<typeof Button>, 'variant'> & {
  sx?: SxProps<Theme>;
  tooltip?: string;
  tooltipPlacement?: TooltipProps['placement'];
};

const GradientActionButton: FC<GradientActionButtonProps> = ({
  sx,
  tooltip,
  tooltipPlacement = 'top',
  ...rest
}) => {
  const sxArray = Array.isArray(sx) ? sx : sx ? [sx] : [];
  const isLink = typeof rest.href === 'string' && rest.href.length > 0;

  const button = (
    <Button
      variant="contained"
      {...rest}
      sx={[
        {
          background: gradientBackground,
          color: 'common.white',
          borderRadius: '12px',
          px: '22px',
          py: '8px',
          fontFamily: 'Basis Grotesque',
          fontWeight: 700,
          fontSize: '15px',
          lineHeight: '26px',
          letterSpacing: '0.4px',
          textTransform: 'none',
          '&:hover': {
            background: gradientBackground,
            opacity: 0.9,
          },
          '&.Mui-disabled': {
            background: gradientBackground,
            color: 'common.white',
            opacity: 1,
            cursor: 'not-allowed',
            ...(isLink ? {} : { pointerEvents: 'auto' }),
          },
          '&.Mui-disabled:hover': {
            background: gradientBackground,
            opacity: 1,
          },
        },
        ...sxArray,
      ]}
    />
  );

  if (!tooltip) {
    return button;
  }

  return (
    <Tooltip title={tooltip} placement={tooltipPlacement}>
      <span style={{ display: 'inline-flex' }}>{button}</span>
    </Tooltip>
  );
};

GradientActionButton.displayName = 'GradientActionButton';

export default GradientActionButton;
export type { GradientActionButtonProps };
